import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@hub/ui/components/ui/card";
import { OidcRedirect } from "@hub/ui/components/OidcRedirect";
import { authService } from "@hub/api/authService";
import { setTokens } from "@hub/api/client";
import { createTypedClient } from "@hub/api/services";
import { logError } from "@hub/shared/utils/errorLogger";
import type { LookupResponse } from "@hub/api/types";
import { ConnectError } from "@connectrpc/connect";
import { AuthService } from "@hub/proto/auth/v1/auth_pb";
import { EmailStep } from "./EmailStep";
import { PasswordStep } from "./PasswordStep";
import { useAuth } from "../useAuth";
import { HubMark } from "./HubMark";

// The single login path (HUB-362).
//
// Console and Workbench each carried a ~200-line copy of this. They had drifted
// in four places, and NEITHER copy was wholly correct — each had something the
// other was missing, which is the reason a shared component matters more here
// than in ordinary UI code:
//
//   1. Transport. Console used @hub/api's createTypedClient; Workbench built its
//      own createConnectTransport and cast the service through `any` with an
//      eslint-disable. Anything fixed in @hub/api's transport — interceptors,
//      token refresh, base URL — reached Console and silently missed Workbench.
//      Resolved in favour of createTypedClient.
//
//   2. API-key redirect. Console validated `from` against open redirect
//      (must start with a single "/"); Workbench did not. Console's guard wins.
//
//   3. Post-authentication navigation. Console did window.location.href = path,
//      a full reload — which discards the in-memory tokens setTokens() had just
//      stored. Workbench refreshed the auth context and used navigate(), which
//      preserves them. Workbench's approach wins.
//
//   4. Password handling. Console cleared the password from state after a
//      successful submit and left it populated on failure so the user can
//      retry; Workbench never cleared it. Console's PasswordStep wins.
//
// So this component is deliberately not a copy of either. Points 2 and 3
// together are a real fix: the previous Console flow could authenticate and
// then land the user back on the login page with no token.

const authClient = createTypedClient(AuthService);

/** Extract a user-facing message without leaking whether an account exists. */
function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ConnectError) {
    return err.message || fallback;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  return fallback;
}

/**
 * isSafeRedirect rejects anything that is not a single-slash relative path.
 *
 * "//evil.com" is protocol-relative and navigates off-site, so the leading
 * double slash must be refused explicitly — checking only for a leading "/"
 * is not enough.
 */
function isSafeRedirect(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//");
}

type LoginStep = "email" | "password" | "oidc";

export interface LoginPageProps {
  /**
   * Product name in the heading — "Hub", "Workbench". The only thing that
   * legitimately differed between the two copies.
   */
  appName: string;
}

export function LoginPage({ appName }: LoginPageProps) {
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [lookupResult, setLookupResult] = useState<LookupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const requested = (location.state as { from?: Location })?.from?.pathname || "/";
  const from = isSafeRedirect(requested) ? requested : "/";

  const handleEmailSubmit = async (submittedEmail: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.lookup(submittedEmail);
      setEmail(submittedEmail);
      setLookupResult(result);
      setStep(result.provider === "local" ? "password" : "oidc");
    } catch (err) {
      logError("LoginPage.handleEmailSubmit", err);
      const message = getErrorMessage(err, "");
      // A JSON parse failure means the endpoint is not there at all, which is
      // worth saying — it is a deployment problem, not a credential problem.
      if (message.includes("Unexpected token") || message.includes("not valid JSON")) {
        setError("Email login is not available. Please use an API key to sign in.");
      } else {
        // Generic message to prevent user enumeration.
        setError("Authentication failed. Please check your credentials and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (password: string) => {
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      logError("LoginPage.handlePasswordSubmit", err);
      // Generic message to prevent user enumeration.
      setError("Authentication failed. Please check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    setLookupResult(null);
    setError(null);
  };

  const handleApiKeySubmit = async (apiKey: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authClient.authenticateAPIKey({ apiKey });

      if (!response.accessToken) {
        setError("Authentication failed. Please check your credentials and try again.");
        return;
      }

      // Validate before storing, so an unusable token never reaches the client.
      const validateResponse = await authClient.validateToken({ token: response.accessToken });

      if (!validateResponse.valid) {
        setError("Authentication failed. Please check your credentials and try again.");
        return;
      }

      // Store, then refresh the auth context in place. A full page reload here
      // would discard the tokens we just put in memory.
      setTokens(response.accessToken, response.refreshToken);
      await refresh();
      navigate(from, { replace: true });
    } catch (err) {
      logError("LoginPage.handleApiKeySubmit", err);
      setError("Authentication failed. Please check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gradient-to-br dark:from-coronis-navy-800 dark:to-coronis-navy-600 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <HubMark />
          </div>
          <CardTitle>Sign in to {appName}</CardTitle>
          <CardDescription>Enter your email to continue</CardDescription>
        </CardHeader>
        <CardContent>
          {step === "email" && (
            <EmailStep
              onSubmit={handleEmailSubmit}
              onApiKeySubmit={handleApiKeySubmit}
              loading={loading}
              error={error}
            />
          )}
          {step === "password" && lookupResult && (
            <PasswordStep
              email={email}
              displayName={lookupResult.displayName}
              onSubmit={handlePasswordSubmit}
              onBack={handleBack}
              loading={loading}
              error={error}
            />
          )}
          {step === "oidc" && lookupResult && (
            <OidcRedirect
              providerName={lookupResult.providerName || "your identity provider"}
              redirectUrl={lookupResult.redirectUrl || ""}
              onBack={handleBack}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
